import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import { Box, Menu, Grid, Typography, Divider, MenuItem, styled } from '@mui/material';
import { useMenu } from '../../../components/base/useMenu';
import { formatDateFromTimestamp } from '../../TestPage/components/TestDetails';
import { useTheme } from '@emotion/react';
import useAwaitModal from '../../../hooks/useAwaitModal';
import LaunchParamsModal from './LaunchParamsModal';
import LaunchIcon from '@mui/icons-material/Launch';


const DetailMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: 320,
    borderRadius: '12px',
    boxShadow: theme.shadows[4],
    padding: theme.spacing(1, 0),
    backgroundColor: theme.palette.background.paper,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      right: 14,
      top: -10,
      width: 20,
      height: 20,
      backgroundColor: theme.palette.background.paper,
      transform: 'rotate(45deg)',
      zIndex: 0
    }
  }
}));

const DetailGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1, 2)
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem'
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.8rem',
  wordBreak: 'break-word'
}));

const ActionMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  marginTop: theme.spacing(1),
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const TestDetails = ({ activeFolder, name, appetizeClient, testResponse }) => {
  const { triggerProps, menuProps } = useMenu();
  const theme = useTheme();

  const [
    requestLaunchParamsModal,
    {
      open: openLaunchParamsModal,
      onClose: onCloseLaunchParamsModal,
      onComplete: completeLaunchParamsModal
    }
  ] = useAwaitModal();

  const onClickLaunchParamsModal = () => {
    requestLaunchParamsModal();
    menuProps.onClose();
  };

  return (
    <Box>
      <IconButton
        aria-label="Test details"
        aria-controls={menuProps.open ? 'test-details-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuProps.open ? 'true' : undefined}
        {...triggerProps}
        sx={{
          '&:hover': {
            backgroundColor: theme.palette.action.hover
          }
        }}>
        <MoreVertIcon sx={{ color: theme.palette.text.secondary }} />
      </IconButton>

      <DetailMenu
        id="test-details-menu"
        {...menuProps}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Test Case Details
          </Typography>

          <DetailGrid container spacing={1}>
            <DetailGrid item xs={5}>
              <DetailLabel>App name:</DetailLabel>
            </DetailGrid>
            <DetailGrid item xs={7}>
              <DetailValue>{appetizeClient?.app?.name}</DetailValue>
            </DetailGrid>

            <DetailGrid item xs={5}>
              <DetailLabel>Folder name:</DetailLabel>
            </DetailGrid>
            <DetailGrid item xs={7}>
              <DetailValue>{activeFolder?.name}</DetailValue>
            </DetailGrid>

            <DetailGrid item xs={5}>
              <DetailLabel>Test case name:</DetailLabel>
            </DetailGrid>
            <DetailGrid item xs={7}>
              <DetailValue>{name}</DetailValue>
            </DetailGrid>

            <DetailGrid item xs={5}>
              <DetailLabel>Created By:</DetailLabel>
            </DetailGrid>
            <DetailGrid item xs={7}>
              <DetailValue textTransform="capitalize">
                {testResponse?.data?.creator?.name}
              </DetailValue>
            </DetailGrid>

            <DetailGrid item xs={5}>
              <DetailLabel>Date Created:</DetailLabel>
            </DetailGrid>
            <DetailGrid item xs={7}>
              <DetailValue>{formatDateFromTimestamp(testResponse?.data?.created_at)}</DetailValue>
            </DetailGrid>

            <DetailGrid item xs={5}>
              <DetailLabel>Last Modified:</DetailLabel>
            </DetailGrid>
            <DetailGrid item xs={7}>
              <DetailValue>{formatDateFromTimestamp(testResponse?.data?.updated_at)}</DetailValue>
            </DetailGrid>

            <DetailGrid item xs={5}>
              <DetailLabel>Last Run:</DetailLabel>
            </DetailGrid>
            <DetailGrid item xs={7}>
              <DetailValue>{formatDateFromTimestamp(testResponse?.data?.last_run_at)}</DetailValue>
            </DetailGrid>
          </DetailGrid>

          <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />

          <ActionMenuItem onClick={onClickLaunchParamsModal}>
            <LaunchIcon fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
              Launch Query Params
            </Typography>
          </ActionMenuItem>
        </Box>
      </DetailMenu>

      <LaunchParamsModal
        open={openLaunchParamsModal}
        onClose={onCloseLaunchParamsModal}
        onComplete={completeLaunchParamsModal}
        client={appetizeClient}
        mobileAppId={testResponse?.data?.mobile_app?.id}
        testData={testResponse?.data}
      />
    </Box>
  );
};

export default TestDetails;
